import React, { useState } from 'react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { recordIwi } from '../../services/iwi.service';
import { IwiItemRef, IWI_NOTE_MAX } from '../../types/iwi.types';

interface Props {
  item: IwiItemRef;
  lastKnownSaleId: string | null;
  lastKnownSaleTitle: string | null;
  /** When the item last changed hands. Shown on screen — never implied. */
  lastKnownSaleEndedAt: Date | null;
  source: 'past-auction-lot' | 'sku-verify';
}

const emailLooksValid = (e: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());

/** "16 August 2026", or null when we genuinely don't know. Never guessed. */
const asOf = (d: Date | null): string | null => {
  if (!d || Number.isNaN(d.getTime())) return null;
  try { return format(d, 'd MMMM yyyy'); } catch { return null; }
};

/**
 * I WANT IT — Phase 0.
 *
 * Records interest. Sends nothing. The copy says so plainly, because the whole
 * point of collecting this first is that we do not yet know whether we can
 * reach anyone, and promising delivery we cannot make would poison the signal
 * we are trying to gather.
 *
 * OWNERSHIP LANGUAGE: this component never says "the owner". It says "last
 * known owner, as of <date>" with the date rendered, and when we have no date
 * it says we do not know. The claim is only ever as strong as the sale it came
 * from — anyone can disprove it, and the item may have moved since.
 */
const IWantItButton: React.FC<Props> = ({
  item, lastKnownSaleId, lastKnownSaleTitle, lastKnownSaleEndedAt, source,
}) => {
  const { currentUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [recorded, setRecorded] = useState(false);

  const dateLabel = asOf(lastKnownSaleEndedAt);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser && !emailLooksValid(email)) {
      toast.error('Please enter an email so we can come back to you.');
      return;
    }
    setSubmitting(true);
    const res = await recordIwi({
      item,
      kind: 'ping',
      amountDollars: amount,
      note,
      requesterUid: currentUser?.uid || null,
      requesterEmail: currentUser ? null : email,
      lastKnownSaleId,
      lastKnownSaleTitle,
      lastKnownSaleEndedAt,
      source,
    });
    setSubmitting(false);
    if (!res.ok) {
      // Surfaced, never swallowed — we told them it would be recorded.
      toast.error('We could not record that just now. Nothing was saved — please try again.');
      return;
    }
    setRecorded(true);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center justify-center min-h-[44px] rounded-md bg-gray-900 text-white font-semibold text-sm px-5 py-2.5 hover:bg-gray-800 transition-colors"
      >
        I Want It
      </button>
    );
  }

  if (recorded) {
    return (
      <div className="rounded-lg border border-green-300 bg-green-50 p-4">
        <p className="font-semibold text-green-900">Recorded.</p>
        <p className="text-sm text-green-800 mt-1">
          Your interest is on this item. <strong>Nothing has been sent to anyone</strong> — we are
          collecting interest first and building owner contact afterwards, so that when we do reach
          out we already know what people are asking for.
        </p>
        <p className="text-sm text-green-800 mt-2">
          If owner contact ships, this is the queue it comes from. We will email you either way.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-lg border border-gray-300 bg-white p-4 space-y-3">
      <div>
        <h3 className="font-bold text-gray-900">I Want It</h3>
        <p className="text-sm text-gray-600 mt-1">
          Tell us you want this and we will record it against the item.{' '}
          <strong>Nothing is sent to anyone yet.</strong>
        </p>
      </div>

      <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded p-3">
        {dateLabel ? (
          <>
            If we build owner contact, this would go to the <strong>last known owner as of{' '}
            {dateLabel}</strong>
            {lastKnownSaleTitle ? <> — whoever bought it in {lastKnownSaleTitle}</> : null}. It may
            have changed hands since; we would not know.
          </>
        ) : (
          <>We do not have a reliable date for when this item last changed hands, so we cannot say
          who holds it now.</>
        )}
      </p>

      {!currentUser && (
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Your email</span>
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full min-h-[44px] rounded-md border border-gray-300 px-3 py-2 text-sm"
            placeholder="you@example.com" required
          />
        </label>
      )}

      <label className="block">
        <span className="text-sm font-medium text-gray-700">What would you pay? (optional)</span>
        <input
          type="text" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)}
          className="mt-1 w-full min-h-[44px] rounded-md border border-gray-300 px-3 py-2 text-sm"
          placeholder="e.g. 250"
        />
        <span className="text-xs text-gray-500">
          A number helps enormously, and you are not committing to it.
        </span>
      </label>

      <label className="block">
        <span className="text-sm font-medium text-gray-700">Anything to add? (optional)</span>
        <textarea
          value={note} onChange={(e) => setNote(e.target.value.slice(0, IWI_NOTE_MAX))} rows={3}
          className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          placeholder="Why you want it, condition you'd accept, anything useful."
        />
        <span className="text-xs text-gray-500">{note.length}/{IWI_NOTE_MAX}</span>
      </label>

      <div className="flex gap-2">
        <button
          type="submit" disabled={submitting}
          className="inline-flex items-center justify-center min-h-[44px] rounded-md bg-gray-900 text-white font-semibold text-sm px-5 py-2.5 hover:bg-gray-800 disabled:opacity-60"
        >
          {submitting ? 'Recording…' : 'Record my interest'}
        </button>
        <button
          type="button" onClick={() => setOpen(false)}
          className="inline-flex items-center justify-center min-h-[44px] rounded-md border border-gray-300 text-gray-700 text-sm px-4 py-2.5 hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default IWantItButton;
